/**
 * Trigger-eligibility queries for conditional visibility rules in the
 * builder: which earlier items may act as triggers for a checklist position.
 * A question's trigger shares the dependent's section-unit (the root zone
 * counts as its own unit); a conditioned section's trigger is root-zone
 * only. Placement validation (getVisibilityRuleErrors) and dependents
 * queries (conditionalRemoval) consume these predicates; rule execution at
 * runtime lives in frontend-js-shared.
 */
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

export const ALLOWED_TRIGGER_ITEM_TYPES = Object.freeze(
  new Set([
    ITEM_TYPES.RADIOS,
    ITEM_TYPES.SELECT,
    ITEM_TYPES.CHECKBOXES,
    ITEM_TYPES.NUMBER,
    ITEM_TYPES.SCALE,
  ])
)

export function findSectionStart({ items, itemIndex }) {
  const precedingSeparatorIndex = items
    .slice(0, itemIndex)
    .reverse()
    .findIndex(item => item?.type === ITEM_TYPES.SEPARATOR)

  if (precedingSeparatorIndex === -1) {
    return null
  }

  return itemIndex - 1 - precedingSeparatorIndex
}

export function isAllowedTriggerItem(item) {
  return ALLOWED_TRIGGER_ITEM_TYPES.has(item?.type)
}

export default function getEligibleTriggerQuestions({
  items,
  conditionedItemIndex,
}) {
  if (!_.isArray(items) || !_.isNumber(conditionedItemIndex)) {
    return []
  }

  const conditionedItem = items[conditionedItemIndex]

  if (!conditionedItem) {
    return []
  }

  const conditionedIsSeparator = conditionedItem.type === ITEM_TYPES.SEPARATOR
  const conditionedSectionStart = findSectionStart({
    items,
    itemIndex: conditionedItemIndex,
  })

  return items.reduce((candidates, item, index) => {
    if (index >= conditionedItemIndex) {
      return candidates
    }

    if (
      !isAllowedTriggerItem(item) ||
      utils.checklists.hasVisibilityRule(item)
    ) {
      return candidates
    }

    // Section triggers: root zone only.
    if (
      conditionedIsSeparator &&
      findSectionStart({ items, itemIndex: index }) !== null
    ) {
      return candidates
    }

    if (conditionedIsSeparator) {
      candidates.push(item)
      return candidates
    }

    // Question triggers share the dependent's section-unit.
    if (
      findSectionStart({ items, itemIndex: index }) !== conditionedSectionStart
    ) {
      return candidates
    }

    candidates.push(item)
    return candidates
  }, [])
}
